import {
  ApiResponse,
  Budget,
  BudgetServiceLine,
  BudgetCreatePayload,
  BudgetListMeta,
  BudgetListParams,
  BudgetUpdatePayload,
} from "../types";
import { apiClient } from "./apiClient";
import { parseBudgetPrice, normalizeBudgetSubServices } from "../utils/budgetUtils";

function normalizeBudgetItem(line: Record<string, unknown>): BudgetServiceLine {
  const item = line as BudgetServiceLine;
  if (!Array.isArray(item.subServices) || item.subServices.length === 0) {
    return item;
  }

  return {
    ...item,
    subServices: normalizeBudgetSubServices(item),
  };
}

function extractBudgetItems(raw: Record<string, unknown>): Budget["services"] {
  let items: BudgetServiceLine[] = [];
  if (Array.isArray(raw.items)) {
    items = raw.items as BudgetServiceLine[];
  } else if (Array.isArray(raw.services)) {
    items = raw.services as BudgetServiceLine[];
  } else if (Array.isArray(raw.budgetServices)) {
    items = raw.budgetServices as BudgetServiceLine[];
  }

  return items.map((line) =>
    normalizeBudgetItem(line as unknown as Record<string, unknown>)
  );
}

function normalizeBudget(raw: Record<string, unknown>): Budget {
  const services = extractBudgetItems(raw) ?? [];
  const computedTotal = services.reduce(
    (sum, line) => sum + parseBudgetPrice(line?.price),
    0
  );

  return {
    ...(raw as Budget),
    services,
    total: raw.total ?? (computedTotal > 0 ? computedTotal : undefined),
  };
}

function normalizeBudgetList(response: unknown): Budget[] {
  if (Array.isArray(response)) {
    return response.map((item) =>
      normalizeBudget(item as Record<string, unknown>)
    );
  }

  const wrapped = response as { data?: unknown };
  if (Array.isArray(wrapped?.data)) {
    return wrapped.data.map((item) =>
      normalizeBudget(item as Record<string, unknown>)
    );
  }

  return [];
}

function normalizeBudgetMeta(
  response: Record<string, unknown>,
  page: number,
  limit: number,
  items: Budget[]
): BudgetListMeta {
  const raw = (response.meta ?? response.pagination) as
    | Record<string, unknown>
    | undefined;

  const total = Number(
    raw?.total ?? raw?.count ?? raw?.totalCount ?? items.length
  );
  const currentPage = Number(raw?.page ?? page) || 1;
  const pageLimit = Number(raw?.limit ?? limit) || limit;
  const totalPages = Math.max(
    1,
    Number(
      raw?.totalPages ??
        raw?.totalPage ??
        Math.ceil(total / pageLimit)
    ) || 1
  );

  return {
    page: currentPage,
    limit: pageLimit,
    total: Number.isFinite(total) ? total : items.length,
    totalPages,
  };
}

function applyBudgetPagination(
  items: Budget[],
  page: number,
  limit: number,
  response: Record<string, unknown>
): { data: Budget[]; meta: BudgetListMeta } {
  const meta = normalizeBudgetMeta(response, page, limit, items);
  const hasServerMeta = Boolean(response.meta ?? response.pagination);

  if (hasServerMeta || items.length <= limit) {
    return { data: items, meta };
  }

  // API returned the full list without meta — paginate on the client
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * limit;

  return {
    data: items.slice(start, start + limit),
    meta: {
      page: safePage,
      limit,
      total,
      totalPages,
    },
  };
}

export const budgetsApi = {
  async createBudget(
    payload: BudgetCreatePayload
  ): Promise<ApiResponse<Budget>> {
    try {
      const data = await apiClient.post<{ data: Budget }>("/budgets", payload);
      return { success: true, data: normalizeBudget(data.data as unknown as Record<string, unknown>) };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Erro ao criar orçamento",
      };
    }
  },

  async getBudgets(
    params: BudgetListParams = {}
  ): Promise<ApiResponse<Budget[]> & { meta?: BudgetListMeta }> {
    try {
      const query = new URLSearchParams();
      if (params.clientId != null) {
        query.set("clientId", String(params.clientId));
      }
      if (params.status) {
        query.set("status", params.status);
      }
      if (params.page != null) {
        query.set("page", String(params.page));
      }
      if (params.limit != null) {
        query.set("limit", String(params.limit));
      }

      const qs = query.toString();
      const page = params.page ?? 1;
      const limit = params.limit ?? 20;
      const response = await apiClient.get<Record<string, unknown>>(
        `/budgets${qs ? `?${qs}` : ""}`
      );

      const items = normalizeBudgetList(response);
      const { data, meta } = applyBudgetPagination(
        items,
        page,
        limit,
        response
      );

      return {
        success: true,
        data,
        meta,
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Erro ao carregar orçamentos",
      };
    }
  },

  async getBudgetById(id: number): Promise<ApiResponse<Budget>> {
    try {
      const data = await apiClient.get<{ data: Budget }>(`/budgets/${id}`);
      return {
        success: true,
        data: normalizeBudget(data.data as unknown as Record<string, unknown>),
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Erro ao carregar orçamento",
      };
    }
  },

  async updateBudget(
    id: number,
    payload: BudgetUpdatePayload
  ): Promise<ApiResponse<Budget>> {
    try {
      const data = await apiClient.put<{ data: Budget }>(
        `/budgets/${id}`,
        payload
      );
      return {
        success: true,
        data: normalizeBudget(data.data as unknown as Record<string, unknown>),
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Erro ao atualizar orçamento",
      };
    }
  },

  async cancelBudget(id: number): Promise<ApiResponse<Budget>> {
    try {
      const data = await apiClient.delete<{ data: Budget }>(`/budgets/${id}`);
      return {
        success: true,
        data: normalizeBudget(data.data as unknown as Record<string, unknown>),
      };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Erro ao cancelar orçamento",
      };
    }
  },
};
